/**
 * Confronto de notas de faturamento contra a Base de Conhecimento Fiscal.
 *
 *   const ncms = ncmsDosArquivos(arquivos);           // para carregar só a NCM necessária
 *   const base = await carregarBase(supabase, ncms);
 *   const resultado = confrontar(arquivos, base);
 *
 * Nada aqui é parecer fiscal: o confronto aponta incompatibilidades com a base oficial carregada.
 */
import type { BaseConhecimento } from './base';
import { ArquivoInvalido, extrairXmlPorChave as extrairXmlNfePorChave, lerArquivoNfe } from './nfe';
import { extrairEventosNfsePorChave, extrairXmlNfsePorChave, lerArquivoNfse, lerEventosNfse, pareceNfse } from './nfse';
import { RET_ISSQN, SIMPLES_NFSE, TRIB_ISSQN } from './regras-servico';
import { aplicarObservacoes, contextoDoItem } from './observacoes';
import { Avaliacao, avaliarItem, avaliarNota, piorResultado } from './regras';
import type { ArquivoRejeitado, CenarioResumo, EventoNfseLido, ItemLido, NotaAvaliada, NotaLida, Resultado, ResultadoConfronto } from './tipos';
import { ehNfse } from './tipos';

export { carregarBase } from './base';
export type { BaseConhecimento } from './base';
export { carregarConfronto, excluirConfronto, salvarConfronto, buscarXmlNota, type DadosLote } from './persistencia';
export { lerArquivoNfe, lerArquivoNfse };

/** Lê NF-e/NFC-e ou NFS-e do padrão nacional, conforme o conteúdo do arquivo. */
export function lerArquivoNota(conteudo: string, arquivo: string): NotaLida[] {
  return pareceNfse(conteudo) ? lerArquivoNfse(conteudo, arquivo) : lerArquivoNfe(conteudo, arquivo);
}

/** XML bruto de cada nota do arquivo (NF-e, NFC-e ou NFS-e), pela chave de acesso. */
export function extrairXmlPorChave(conteudo: string): Map<string, string> {
  return pareceNfse(conteudo) ? extrairXmlNfsePorChave(conteudo) : extrairXmlNfePorChave(conteudo);
}

/**
 * XML bruto de cada nota de um conjunto de arquivos. O evento de cancelamento de uma NFS-e (que vem em arquivo próprio)
 * é anexado ao XML da nota: guardado junto, ele volta a valer quando o confronto é reavaliado. Arquivo ilegível é pulado
 * (a extração só serve para guardar o XML, DANFE e validador — não derruba a análise).
 */
export function xmlsDasEntradas(entradas: ArquivoEntrada[]): Map<string, string> {
  const mapa = new Map<string, string>();
  const eventos = new Map<string, string[]>();
  for (const entrada of entradas) {
    try {
      for (const [chave, xml] of extrairXmlPorChave(entrada.conteudo)) mapa.set(chave, xml);
      if (pareceNfse(entrada.conteudo)) for (const [chave, blocos] of extrairEventosNfsePorChave(entrada.conteudo)) eventos.set(chave, [...(eventos.get(chave) ?? []), ...blocos]);
    } catch {
      // ignora: ver acima
    }
  }
  for (const [chave, blocos] of eventos) {
    const xml = mapa.get(chave);
    if (xml && !blocos.every((b) => xml.includes(b))) mapa.set(chave, [xml, ...blocos.filter((b) => !xml.includes(b))].join('\n'));
  }
  return mapa;
}
export { ehServico, linhasDoResultado, montarVisao, type CenarioVisao, type LinhaItem, type RegraVisao, type VisaoConfronto } from './visao';
export { itemLc116, ufDoMunicipio } from './nfse';
export { RET_ISSQN, SIMPLES_NFSE, TRIB_ISSQN } from './regras-servico';
export * from './tipos';
export * from './observacoes';

export const VERSAO_MOTOR = 'confronto-2026.10.09.2';

export type ArquivoEntrada = { nome: string; conteudo: string };

/** Máximo de XMLs por confronto: acima disso o navegador fica lento para ler, analisar e salvar. Divida em períodos. */
export const MAX_XMLS_POR_CONFRONTO = 3000;

const traco = (v: string | null | undefined) => (v === null || v === undefined || v === '' ? '—' : v);

/**
 * Chave do cenário: a configuração fiscal do item. Dois itens com a mesma chave são o mesmo cenário,
 * independentemente de produto, valor ou NCM (NCM é contado à parte).
 */
export function chaveCenario(nota: NotaLida, item: ItemLido): string {
  if (ehNfse(nota)) return chaveCenarioServico(nota, item);
  const icms = item.icms ? `${item.icms.grupo}/${traco(item.icms.cst)}` : 'ICMS —';
  const ibs = item.ibscbs ? `IBSCBS ${traco(item.ibscbs.cst)}/${traco(item.ibscbs.cClassTrib)}` : null;
  return [
    `${traco(nota.emit.uf)}>${traco(nota.dest.uf)}`,
    `CRT ${traco(nota.emit.crt)}`,
    `tpNF ${traco(nota.tpNF)}`,
    `fin ${traco(nota.finNFe)}`,
    `idDest ${traco(nota.idDest)}`,
    `final ${traco(nota.indFinal)}`,
    `IE ${traco(nota.dest.indIEDest)}`,
    `CFOP ${traco(item.cfop)}`,
    `orig ${traco(item.icms?.orig)}`,
    icms,
    `IPI ${traco(item.ipi?.cst)}`,
    `PIS ${traco(item.pis?.cst)}`,
    `COFINS ${traco(item.cofins?.cst)}`,
    ...(ibs ? [ibs] : []),
  ].join(' · ');
}

/**
 * Cenário da NFS-e: o código do serviço (cTribNac), onde o ISS incide e como (tributação, retenção, Simples), PIS/COFINS
 * e IBS/CBS. A NBS é contada à parte, como a NCM na NF-e.
 */
function chaveCenarioServico(nota: NotaLida, item: ItemLido): string {
  const s = nota.servico;
  const ibs = item.ibscbs ? `IBSCBS ${traco(item.ibscbs.cst)}/${traco(item.ibscbs.cClassTrib)}` : null;
  const rotulo = (mapa: Record<string, string>, v: string | null | undefined) => (v ? (mapa[v] ?? v) : '—');
  const mesmoMunicipio = s?.cLocIncid && s.cLocIncid === nota.emit.enderEmit?.cMun;
  return [
    'NFS-e',
    `Serviço ${traco(s?.cTribNac)}`,
    `ISS em ${traco(s?.cLocIncid)}${s?.cLocIncid ? (mesmoMunicipio ? ' (prestador)' : ' (fora do prestador)') : ''}`,
    `ISS ${rotulo(TRIB_ISSQN, s?.tribISSQN)}`,
    `ISS ${rotulo(RET_ISSQN, s?.tpRetISSQN)}`,
    `Simples ${rotulo(SIMPLES_NFSE, s?.prestador.opSimpNac)}`,
    `PIS/COFINS ${traco(item.pis?.cst)}`,
    ...(ibs ? [ibs] : []),
  ].join(' · ');
}

/** Lê os arquivos sem avaliar: separa notas válidas, duplicadas e arquivos rejeitados. */
export function lerArquivos(arquivos: ArquivoEntrada[]): { notas: NotaLida[]; rejeitados: ArquivoRejeitado[] } {
  const notas: NotaLida[] = [];
  const rejeitados: ArquivoRejeitado[] = [];
  const porChave = new Map<string, string>();
  /** Cancelamentos de NFS-e do lote, pela chave da nota, com o arquivo de onde vieram. */
  const cancelamentos = new Map<string, EventoNfseLido & { arquivo: string }>();
  for (const a of arquivos) {
    try {
      if (pareceNfse(a.conteudo)) for (const ev of lerEventosNfse(a.conteudo)) cancelamentos.set(ev.chave, { ...ev, arquivo: a.nome });
      for (const nota of lerArquivoNota(a.conteudo, a.nome)) {
        const anterior = porChave.get(nota.chave);
        if (anterior) {
          rejeitados.push({ arquivo: a.nome, motivo: `${ehNfse(nota) ? 'NFS-e' : 'NF-e'} duplicada — mesma chave do arquivo ${anterior}` });
          continue;
        }
        porChave.set(nota.chave, a.nome);
        notas.push(nota);
      }
    } catch (e) {
      rejeitados.push({ arquivo: a.nome, motivo: e instanceof ArquivoInvalido ? e.message : `Falha na leitura: ${e instanceof Error ? e.message : String(e)}` });
    }
  }
  const noLote = new Set(notas.map((n) => n.chave));
  for (const nota of notas) {
    const ev = nota.servico ? cancelamentos.get(nota.chave) : undefined;
    if (ev && nota.servico) nota.servico.cancelamento = { chave: ev.chave, codigo: ev.codigo, descricao: ev.descricao, motivo: ev.motivo, dhEvento: ev.dhEvento };
  }
  for (const ev of cancelamentos.values()) {
    if (!noLote.has(ev.chave)) rejeitados.push({ arquivo: ev.arquivo, motivo: `Evento de cancelamento da NFS-e ${ev.chave}, que não está neste lote` });
  }
  return { notas, rejeitados };
}

/** NCMs que aparecem nos arquivos (para carregar só essa parte da nomenclatura). */
export function ncmsDasNotas(notas: NotaLida[]): string[] {
  return [...new Set(notas.flatMap((n) => n.itens.map((i) => i.ncm).filter((x): x is string => !!x)))];
}

const ordemResultado: Record<Resultado, number> = { CONFORME: 0, DEPENDE: 1, DIVERGENTE: 2 };

export function avaliar(notas: NotaLida[], rejeitados: ArquivoRejeitado[], base: BaseConhecimento, qtdArquivos: number): ResultadoConfronto {
  const avaliadas: NotaAvaliada[] = notas.map((nota) => {
    const avNota = new Avaliacao(base);
    avaliarNota(nota, avNota);
    const observacoes = base.observacoes ?? [];
    const achadosNota = aplicarObservacoes(avNota.achados, observacoes, (regra) => contextoDoItem(regra, nota, null, null));
    const itens = nota.itens.map((item) => {
      const av = new Avaliacao(base);
      const cfop = avaliarItem(nota, item, base, av);
      const cenarioChave = chaveCenario(nota, item);
      const achados = aplicarObservacoes(av.achados, observacoes, (regra) => contextoDoItem(regra, nota, item, cenarioChave));
      return {
        item,
        cenarioChave,
        naturezaCodigo: cfop?.naturezas[0] ?? null,
        achados,
        pior: piorResultado([...achados, ...achadosNota]),
      };
    });
    return { nota, achados: achadosNota, itens, pior: piorResultado([...achadosNota, ...itens.flatMap((i) => i.achados)]) };
  });

  const cenarios = new Map<string, CenarioResumo & { notas: Set<string>; ncmSet: Set<string>; regraSet: Set<string> }>();
  for (const n of avaliadas) {
    for (const i of n.itens) {
      const c = cenarios.get(i.cenarioChave) ?? {
        chave: i.cenarioChave,
        modelo: n.nota.modelo,
        cfop: i.item.cfop,
        servico: n.nota.servico?.cTribNac ?? null,
        naturezaCodigo: i.naturezaCodigo,
        qtdItens: 0,
        qtdNotas: 0,
        ncms: [],
        vProd: 0,
        pior: 'CONFORME' as Resultado,
        regras: [],
        notas: new Set<string>(),
        ncmSet: new Set<string>(),
        regraSet: new Set<string>(),
      };
      c.qtdItens += 1;
      c.notas.add(n.nota.chave);
      const classificacao = ehNfse(n.nota) ? n.nota.servico?.cNBS : i.item.ncm;
      if (classificacao) c.ncmSet.add(classificacao);
      c.vProd += i.item.vProd ?? 0;
      [...n.achados, ...i.achados].forEach((a) => c.regraSet.add(a.regra));
      if (ordemResultado[i.pior] > ordemResultado[c.pior]) c.pior = i.pior;
      cenarios.set(i.cenarioChave, c);
    }
  }
  const listaCenarios: CenarioResumo[] = [...cenarios.values()]
    .map(({ notas: ns, ncmSet, regraSet, ...c }) => ({ ...c, qtdNotas: ns.size, ncms: [...ncmSet].sort(), regras: [...regraSet].sort() }))
    .sort((a, b) => ordemResultado[b.pior] - ordemResultado[a.pior] || b.qtdItens - a.qtdItens);

  const comJustificados = avaliadas.flatMap((n) => [...n.achados, ...n.itens.flatMap((i) => i.achados)]);
  const todos = comJustificados.filter((a) => !a.justificado);
  return {
    versaoMotor: VERSAO_MOTOR,
    notas: avaliadas,
    rejeitados,
    cenarios: listaCenarios,
    totais: {
      arquivos: qtdArquivos,
      notas: avaliadas.length,
      itens: avaliadas.reduce((s, n) => s + n.itens.length, 0),
      cenarios: listaCenarios.length,
      erros: todos.filter((a) => a.severidade === 'erro').length,
      alertas: todos.filter((a) => a.severidade === 'alerta').length,
      infos: todos.filter((a) => a.severidade === 'info').length,
      justificados: comJustificados.length - todos.length,
    },
  };
}

/** Atalho: lê e avalia. A base precisa ter sido carregada com as NCMs destes arquivos. */
export function confrontar(arquivos: ArquivoEntrada[], base: BaseConhecimento): ResultadoConfronto {
  const { notas, rejeitados } = lerArquivos(arquivos);
  return avaliar(notas, rejeitados, base, arquivos.length);
}
